import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, ArrowRight, Envelope, DeviceMobile, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import VetrichLogo from "@/components/VetrichLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatErr(err) {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || "").filter(Boolean).join(" ");
  return err?.message || "Something went wrong";
}

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);           // 1 = ask channel, 2 = enter OTP + new pw
  const [channel, setChannel] = useState("email");
  const [identifier, setIdentifier] = useState("");
  const [otp, setOtp] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [busy, setBusy] = useState(false);

  const sendOtp = async (e) => {
    e.preventDefault();
    if (!identifier.trim()) { toast.error("Enter your email or mobile number"); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/forgot-password`,
        { identifier: identifier.trim(), channel },
        { withCredentials: true });
      toast.success(data.message || "OTP sent");
      setStep(2);
    } catch (err) { toast.error(formatErr(err)); }
    finally { setBusy(false); }
  };

  const resetPw = async (e) => {
    e.preventDefault();
    if (newPw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (newPw !== confirmPw) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/reset-password`,
        { identifier: identifier.trim(), otp: otp.trim(), new_password: newPw },
        { withCredentials: true });
      toast.success(data.message || "Password reset. Please sign in.");
      navigate("/login", { replace: true });
    } catch (err) { toast.error(formatErr(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="relative hidden lg:block auth-bg">
        <div className="absolute inset-0 grid-tile opacity-40" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="inline-flex self-start bg-white rounded-md shadow-lg p-3 pr-5 items-center">
            <VetrichLogo size={64} tone="dark" />
          </div>
          <div className="max-w-lg">
            <div className="eyebrow text-white/70 mb-4">Account Recovery</div>
            <h1 className="font-heading text-4xl lg:text-5xl font-bold tracking-tight leading-[1.02]">
              Reset securely.<br />
              <span className="text-white/70">Get back to business.</span>
            </h1>
            <p className="text-white/80 mt-6 text-[15px] leading-relaxed max-w-md">
              We'll send a one-time passcode to your registered email or mobile number.
              Choose whichever is quickest for you.
            </p>
          </div>
          <div className="text-[11px] text-white/60 tracking-wider">© 2026 Vetrich Technologies · v1.0</div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex items-center justify-center px-6 py-12 bg-background">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex items-center justify-between">
            <VetrichLogo size={48} tone="dark" />
            <Link to="/login" className="text-[11px] uppercase tracking-[0.28em] text-muted-foreground inline-flex items-center gap-1 hover:text-foreground">
              <ArrowLeft size={12} /> Back to sign in
            </Link>
          </div>

          {step === 1 && (
            <form onSubmit={sendOtp}>
              <div className="eyebrow mb-3">Forgot password</div>
              <h2 className="font-heading text-3xl font-bold tracking-tight mb-1">Send me an OTP.</h2>
              <p className="text-sm text-muted-foreground mb-8">
                Enter your email or mobile number registered with Vetrich ERP.
              </p>

              <div className="space-y-5">
                <div className="space-y-1.5">
                  <Label className="eyebrow">Email or Mobile Number</Label>
                  <Input
                    required autoFocus autoComplete="username"
                    value={identifier} onChange={(e) => setIdentifier(e.target.value)}
                    placeholder="you@example.com or +91 98765 43210"
                    className="h-11 font-mono" data-testid="forgot-identifier"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="eyebrow">Send OTP via</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <button type="button"
                      className={`p-3 rounded-md border flex items-center gap-2 text-sm transition ${channel === "email" ? "border-primary bg-primary/5" : "border-border hover:border-foreground/40"}`}
                      onClick={() => setChannel("email")}
                      data-testid="forgot-channel-email">
                      <Envelope size={16} /> Email
                    </button>
                    <button type="button"
                      className={`p-3 rounded-md border flex items-center gap-2 text-sm transition ${channel === "sms" ? "border-primary bg-primary/5" : "border-border hover:border-foreground/40"}`}
                      onClick={() => setChannel("sms")}
                      data-testid="forgot-channel-sms">
                      <DeviceMobile size={16} /> Mobile OTP
                    </button>
                  </div>
                </div>

                <Button type="submit" disabled={busy} className="w-full h-11 group rounded-md" data-testid="forgot-send-btn">
                  {busy ? "Sending…" : "Send OTP"}
                  <ArrowRight weight="bold" size={16} className="ml-2 transition-transform group-hover:translate-x-0.5" />
                </Button>
              </div>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={resetPw}>
              <div className="eyebrow mb-3">Verify & Reset</div>
              <h2 className="font-heading text-3xl font-bold tracking-tight mb-1">Enter the OTP.</h2>
              <p className="text-sm text-muted-foreground mb-8">
                We sent a 6-digit code via <span className="font-semibold">{channel === "sms" ? "SMS" : "email"}</span>.
                It expires in 10 minutes.
              </p>

              <div className="space-y-5">
                <div className="space-y-1.5">
                  <Label className="eyebrow">6-Digit OTP</Label>
                  <Input
                    required autoFocus inputMode="numeric" maxLength={6}
                    value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                    placeholder="123456"
                    className="h-11 font-mono tracking-[0.4em] text-center text-lg"
                    data-testid="reset-otp"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="eyebrow">New Password</Label>
                  <Input required type="password" value={newPw}
                    onChange={(e) => setNewPw(e.target.value)}
                    placeholder="At least 6 characters"
                    className="h-11" data-testid="reset-newpw" />
                </div>
                <div className="space-y-1.5">
                  <Label className="eyebrow">Confirm New Password</Label>
                  <Input required type="password" value={confirmPw}
                    onChange={(e) => setConfirmPw(e.target.value)}
                    className="h-11" data-testid="reset-confirmpw" />
                </div>

                <Button type="submit" disabled={busy} className="w-full h-11 group rounded-md" data-testid="reset-submit-btn">
                  {busy ? "Resetting…" : "Reset Password"}
                  <ArrowRight weight="bold" size={16} className="ml-2 transition-transform group-hover:translate-x-0.5" />
                </Button>
                <button type="button"
                  onClick={() => { setStep(1); setOtp(""); setNewPw(""); setConfirmPw(""); }}
                  className="w-full text-[12px] text-muted-foreground hover:text-foreground"
                  data-testid="reset-back">
                  ← Change email/mobile or resend OTP
                </button>
              </div>
            </form>
          )}

          <div className="mt-8 pt-6 border-t border-border text-[12px] text-muted-foreground flex items-start gap-2.5">
            <ShieldCheck weight="regular" size={16} className="mt-0.5 shrink-0" />
            <span>OTPs are single-use, expire in 10 minutes, and are hashed at rest. Max 5 attempts per code.</span>
          </div>
        </div>
      </div>
    </div>
  );
}
